<template>
  <RevCard class="flex flex-col gap-24 p-24" data-cs-mask>
    <div
      v-for="{
        address,
        title,
        withPhoneNumber,
        to,
        buttonAriaLabel,
      } in addresses"
      :key="title.id"
      class="flex flex-col items-start gap-8 md:flex-row"
    >
      <div class="grow">
        <Address :address compact with-details :with-phone="withPhoneNumber">
          <template #title="{ fullName }">
            {{ i18n(title, { fullName }) }}
          </template>
        </Address>
      </div>

      <RevButton
        v-if="to"
        :aria-label="buttonAriaLabel"
        size="small"
        :to="resolveLocalizedRoute(to)"
        variant="secondary"
      >
        {{ i18n(translations.modify) }}
      </RevButton>
    </div>
  </RevCard>
</template>

<script setup lang="ts">
import { useRouteLocationWithLocale } from '#imports'
import { computed } from 'vue'

import {
  type Bill,
  type Deliver,
  type DeliverCollectionPoint,
  isDeliverCollectionPoint,
} from '@backmarket/nuxt-layer-checkout/api/cart/types'
import Address from '@backmarket/nuxt-module-address/Address.vue'
import { useExperiments } from '@backmarket/nuxt-module-experiments/useExperiments'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { RevButton } from '@ds/components/Button'
import { RevCard } from '@ds/components/Card'

import { CHECKOUT } from '~/scopes/checkout/routes-names'

import translations from './CheckoutSummaryAddressesCard.translations'

const props = defineProps<{
  billing: Bill
  shipping: DeliverCollectionPoint | Deliver
}>()

const i18n = useI18n()
const resolveLocalizedRoute = useRouteLocationWithLocale()
const experiments = useExperiments()

const isSinglePageAddressesEnabled = computed(() => {
  return experiments['experiment.checkoutOnePageAddress'] === 'onePageAddress'
})

const addresses = computed(() => {
  return [
    isDeliverCollectionPoint(props.shipping)
      ? {
          title: translations.shipTo,
          address: {
            ...props.shipping,
            ...props.shipping.collectionPoint,
            company: props.shipping.collectionPoint.name,
          },
          withPhoneNumber: true,
          buttonAriaLabel: i18n(translations.modifyShippingAddress),
        }
      : {
          title: translations.shipTo,
          address: props.shipping,
          withPhoneNumber: true,
          to: {
            name: isSinglePageAddressesEnabled.value
              ? CHECKOUT.ADDRESSES
              : CHECKOUT.SHIPPING_ADDRESS,
          },
          buttonAriaLabel: i18n(translations.modifyShippingAddress),
        },
    {
      title: translations.billTo,
      address: props.billing,
      withPhoneNumber: false,
      to: {
        name: isSinglePageAddressesEnabled.value
          ? CHECKOUT.ADDRESSES
          : CHECKOUT.BILLING_ADDRESS,
      },
      buttonAriaLabel: i18n(translations.modifyBillingAddress),
    },
  ]
})
</script>
